import React, { useEffect, useState, useReducer } from "react"; 
import { OrganizationSelectorModal } from "@atlassiansox/bxpkit-organization-selector-modal";
import Environment from "../../../common/environment";
import storage from "../../../utils/storage";

type AdminHubOrgSelectorDeeplinkerContainerProps = {
    dataUUID: string;
    labels: string;
    labels_english: string;
};

type accountOrg = {
    readonly id?: string;
    readonly name?: string;
    readonly isAdmin?: boolean;
};

type GetAccountOrgsResponse = accountOrg[];

type orgMapsType = {
    [key:string]: any
}

type selectedOrg = {
    id: string;
    name: string;
    isAdmin: boolean;
}

type accountOrgResponseType = {
    orgList: string[];
    orgMap: orgMapsType;
    selectedOrg: selectedOrg;
}

enum StateReducerTypes {
    ISOPENMODAL ='isOpenModal',
}

type deeplinkerContainerStateReducerAction = {
    type: StateReducerTypes;
    payload?: boolean;
}

type deeplinkerContainerStateReducerState = {
    isOpenModal: boolean;
}

type apiMeResponseType = {
    readonly account_id: string;
    readonly email: string;
    readonly name: string;
    readonly picture?: string;
    readonly account_status?: string;
    readonly characteristics?: any;
    readonly last_updated?: string;
    readonly nickname?: string;
    readonly locale?: string;
    readonly extended_profile?: any;
    readonly account_type?: string;
    readonly email_verified?: boolean;
}

type dispatchFunctionType = () => React.Dispatch<any>;

type OrganizationSelectorViewModalProps = {
    dataUUID: string;
    dispatch: React.Dispatch<any>
}

type OrganizationSelectorViewModalButtonProps = {
    buttonLabel: string;
    buttonDataUUID: string;
    buttonLabelEnglish: string;
    dispatch: React.Dispatch<any>
}

const deeplinkerContainerStateReducer = (state: deeplinkerContainerStateReducerState, action: deeplinkerContainerStateReducerAction): any => {
    switch (action.type) {
        case StateReducerTypes.ISOPENMODAL:
            return {
                ...state,
                isOpenModal: action.payload
            };
        default:
            throw Error('Unknown action: ' + action.type);
    }
};

const setIsOpenModal = (dataUUID: string, bool:boolean) => {
    const url = new URL(window.location.href);
    url.searchParams.set(`isModelOpen-${dataUUID}`, `${bool}`);
    window.history.pushState({}, "", url);
};

const onSwitchAccountClick = (dataUUID: string) => {
    setIsOpenModal(dataUUID, true);
    window.location.assign(Environment.getSwitchAccountURL());
}

const OrganizationSelectorViewModal = ({dataUUID, dispatch}: OrganizationSelectorViewModalProps) => {
    const [isLoading, setIsLoading] = useState(true);
    const [accountState, setAccountState] = useState({
        isSetData: false,
        apiMe: {
            account_id: "",
            email: "",
            name: "",
            picture: "",
            account_status: "",
            characteristics: {},
            last_updated: "",
            nickname: "",
            locale: "",
            extended_profile: {},
            account_type: "",
            email_verified: true
        } as apiMeResponseType,
        signupAccountOrgsList: {
            orgList: [],
            orgMap: {},
            selectedOrg: {
                id: '',
                name: '',
                isAdmin: false
            }
        } as accountOrgResponseType
    });

    useEffect(() => {
        getAccontState();
    }, []);

    useEffect(() => {
        if (accountState.signupAccountOrgsList.orgList.length === 1) {
            onOrganizationSelected(accountState.apiMe.email, accountState.signupAccountOrgsList.selectedOrg.id);
            setIsLoading(true);
        } else {
            setIsLoading(false);
        }
    }, [accountState.isSetData]);

    const getAccontState = async () => {
        const defaultSignupAccountOrgsListData : accountOrgResponseType = {
            orgList: [],
            orgMap: {},
            selectedOrg: {
                id: '',
                name: '',
                isAdmin: false
            }
        };
        const getApiMeResponse = await getApiMe();
        const getSignupAccountOrgsListResponse = await getSignupAccountOrgsList();

        if (getSignupAccountOrgsListResponse && getSignupAccountOrgsListResponse.length > 0) {
            const filterIsAdminAccountOrgsResponse = getSignupAccountOrgsListResponse.filter((accountOrg) => accountOrg.isAdmin === true);
            if (filterIsAdminAccountOrgsResponse.length > 0){
                filterIsAdminAccountOrgsResponse.forEach((accountOrg) => {
                    const accountOrgName = accountOrg.name!;
                    defaultSignupAccountOrgsListData.orgMap[accountOrgName] = accountOrg;
                });
                defaultSignupAccountOrgsListData.orgList = Object.keys(defaultSignupAccountOrgsListData.orgMap);
                // Set the first org as the default selected org
                const firstItemInOrgList = defaultSignupAccountOrgsListData.orgList[0];
                const firstMapObject = defaultSignupAccountOrgsListData.orgMap[firstItemInOrgList];
                defaultSignupAccountOrgsListData.selectedOrg = firstMapObject;
            }
        } 

        setAccountState({
            ...accountState,
            ...{
                isSetData: true,
                apiMe: getApiMeResponse,
                signupAccountOrgsList: defaultSignupAccountOrgsListData
            }
        });
    };

    const getApiMe = async () => {
        const getApiMeResponse: apiMeResponseType = await fetch(
            `${Environment.getAPIPrivateURL()}/me`,
            {
                method: "GET",
                credentials: 'include',
                headers: {
                    "Content-type": "application/json; charset=UTF-8",
                },
            }
        ).then((res) => {
            if (res.ok) {
                return res.json();
            }
        })
        .catch();
        return getApiMeResponse;
    };

    const getSignupAccountOrgsList = async () => {
        const signupAccountOrgsList: GetAccountOrgsResponse = await fetch(
            `${Environment.getAPIPrivateURL()}/bxp/signup/account/orgs`,
            {
                method: "GET",
                credentials: 'include',
                headers: {
                    "Content-type": "application/json; charset=UTF-8",
                },
            }
        ).then((res) => {
            if (res.ok) {
                return res.json();
            }
            return [];
        }).catch();
        return signupAccountOrgsList
    };

    const onOrganizationChanged = (selectedOrgName: string) => {
        const selectedOrgObj = accountState.signupAccountOrgsList.orgMap[selectedOrgName];
        setAccountState({
            ...accountState,
            signupAccountOrgsList: {
                orgList: accountState.signupAccountOrgsList.orgList,
                orgMap: accountState.signupAccountOrgsList.orgMap,
                selectedOrg: selectedOrgObj
            } as accountOrgResponseType
        });
    };

    const onOrganizationSelected = (userEmail: string, selectedOrgId: string) => {
        // redirect to identity with continue to adminhub
        const aiUrl = `${Environment.getMyAdminAtlasBaseURL()}/o/${selectedOrgId}/atlassian-intelligence`
        const redirectUrl = `${Environment.getIdentityBaseUrl()}/login??application=wac&continue=${encodeURIComponent(aiUrl)}&login_hint=${encodeURIComponent(userEmail)}&prompt=none`
        window.location.assign(redirectUrl);
    }

    const onCancelClick = () => {
        setIsOpenModal(dataUUID, false);
        return dispatch({type: StateReducerTypes.ISOPENMODAL, payload: false});
    };
    
    return !isLoading ? (
        <OrganizationSelectorModal
            userName={accountState.apiMe?.name || ""}
            userEmail={accountState.apiMe?.email || ""}
            avatarSrc={accountState.apiMe?.picture}
            organizations={accountState.signupAccountOrgsList.orgList}
            isOpen={accountState.isSetData}
            onNewOrganizationSelected={() => {}}
            onOrganizationSelected={() => onOrganizationSelected(accountState.apiMe.email, accountState.signupAccountOrgsList.selectedOrg.id)}
            onSwitchAccountClick={() => onSwitchAccountClick(dataUUID)}
            onOrganizationChanged={onOrganizationChanged as dispatchFunctionType}
            onModalClose={onCancelClick as dispatchFunctionType}
        />
    ) : null;
};


const OrganizationSelectorViewModalButton = ({
    buttonDataUUID,
    buttonLabel,
    buttonLabelEnglish,
    dispatch
}: OrganizationSelectorViewModalButtonProps) => {
    const buttonClickAction = (e) => {
        e.preventDefault();
        if (storage.getCookie("login_user_detected") === "true") {
            dispatch({
                type: StateReducerTypes.ISOPENMODAL, 
                payload: true 
            });
        } else {
            onSwitchAccountClick(buttonDataUUID);
        }
    };
    return (<div className="component component--link-button">
        <a 
            data-event="clicked" 
            data-uuid={buttonDataUUID}
            data-event-component="linkButton" 
            data-event-container="linkButton" 
            data-schema-version="1" 
            data-label={buttonLabel}
            data-label-english={buttonLabelEnglish}
            className="component__link button button--two button--regular button--primary"
            onClick={buttonClickAction}
        >
            {buttonLabel}
        </a>
    </div>);
};

const AdminHubOrgSelectorDeeplinkerContainer = ({
    dataUUID,
    labels,
    labels_english
}: AdminHubOrgSelectorDeeplinkerContainerProps) => {
    const initState = {
        isOpenModal: false
    };
    const [state, dispatch] = useReducer(deeplinkerContainerStateReducer, initState)

    useEffect(() => {
        const url = new URL(window.location.href);
        if(url.searchParams.get(`isModelOpen-${dataUUID}`) === "true") {
            dispatch({
                type: StateReducerTypes.ISOPENMODAL, 
                payload: true 
            });
        }
    }, []);

    return (
        <React.Fragment>
            <OrganizationSelectorViewModalButton
                buttonDataUUID={dataUUID}
                buttonLabel={labels}
                buttonLabelEnglish={labels_english}
                dispatch={dispatch}
            />
            {
                state.isOpenModal
                ? (
                    <OrganizationSelectorViewModal 
                        dataUUID={dataUUID}
                        dispatch={dispatch}
                    />
                )
                : null
            }
        </React.Fragment>
    );
};

export default AdminHubOrgSelectorDeeplinkerContainer;
